import { useEffect, useState, useSyncExternalStore } from 'react';

import {
  isOnline,
  isServerUnreachable,
  subscribeOnline,
  subscribeServerReach,
} from '../model/connectivity';

/**
 * Через сколько после пропажи сети показывать полосу. Мгновенные провалы (лифт, переключение
 * Wi-Fi на мобильную сеть) проходят быстрее, и полоса на них только мигала бы.
 */
export const OFFLINE_BANNER_DELAY_MS = 1500;

/**
 * Что сказать полосой: `offline` - браузер знает, что сети нет; `unreachable` - сеть вроде бы
 * есть, но сервер не отвечает (VPN без сети под ним, мёртвый Wi-Fi).
 */
export type ConnectionTrouble = 'offline' | 'unreachable';

/** На сервере и при гидрации всё в порядке. Иначе разметка сервера и клиента разошлась бы. */
const onlineOnServer = () => true;
const neverUnreachableOnServer = () => false;

/**
 * Какую полосу показывать, `null` - никакую. Появляется через `delayMs` после начала беды и
 * пропадает сразу, как она кончилась. Смена одной беды на другую (сеть вернулась, а сервер ещё
 * молчит) полосу не прячет, меняется только текст.
 */
export const useOfflineBanner = (delayMs = OFFLINE_BANNER_DELAY_MS): ConnectionTrouble | null => {
  const online = useSyncExternalStore(subscribeOnline, isOnline, onlineOnServer);
  const unreachable = useSyncExternalStore(
    subscribeServerReach,
    isServerUnreachable,
    neverUnreachableOnServer
  );
  const trouble: ConnectionTrouble | null = !online
    ? 'offline'
    : unreachable
      ? 'unreachable'
      : null;
  const hasTrouble = trouble !== null;
  const [delayPassed, setDelayPassed] = useState(false);

  useEffect(() => {
    if (!hasTrouble) {
      return undefined;
    }
    const timer = setTimeout(() => setDelayPassed(true), delayMs);

    return () => {
      clearTimeout(timer);
      setDelayPassed(false);
    };
  }, [hasTrouble, delayMs]);

  return delayPassed ? trouble : null;
};
